import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Zap, Plus, ArrowRight, Settings2, Droplets, Wind, Ruler, Thermometer } from 'lucide-react';
import { toast } from 'sonner';

const LaserTools = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMachines = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/laser/machines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMachines(res.data || []);
      } catch {
        toast.error('Failed to load laser machines');
      } finally {
        setLoading(false);
      }
    };
    fetchMachines();
  }, [token]);

  return (
    <div
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.LASER_DASHBOARD)}
    >
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">My Tools</div>
            <h1
              className="text-3xl md:text-4xl font-bold text-[#e67e22] tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              My Laser Equipment
            </h1>
            <p className="mt-2 text-sm text-[#e0e0e0]">
              Your configured laser machines. Used by the calculator and troubleshooting tools.
            </p>
          </div>
          <Link to="/laser/setup">
            <Button className="bg-[#e67e22] hover:bg-[#f39c12] text-black font-semibold flex items-center gap-2 mt-2">
              <Plus className="w-4 h-4" />
              Add Machine
            </Button>
          </Link>
        </div>

        {/* Machine list */}
        {loading ? (
          <div className="text-[#a3a3a3] py-8 text-center">Loading...</div>
        ) : machines.length === 0 ? (
          <Card className="bg-black/80 border-zinc-700">
            <CardContent className="py-10 text-center space-y-4">
              <Wrench className="w-12 h-12 text-zinc-600 mx-auto" />
              <h3 className="text-white font-bold text-lg">No Tools Configured</h3>
              <p className="text-zinc-400 text-sm">Set up your laser equipment first to enable tools, calculator, and troubleshooting features.</p>
              <Link to="/laser/equipment">
                <Button className="bg-[#c85a00] hover:bg-[#a84800] text-white">
                  Go to My Equipment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {machines.map((machine, idx) => (
              <Card key={machine.id || idx} className="bg-black/80 backdrop-blur-sm border border-[#3a3a3a]/50 hover:border-[#e67e22]/50 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-black/60 border border-[#3a3a3a]/50 flex items-center justify-center rounded-sm">
                        <Zap className="w-5 h-5 text-[#e67e22]" />
                      </div>
                      <div>
                        <CardTitle className="text-white text-lg">
                          {machine.name || machine.model || 'Laser Machine'}
                        </CardTitle>
                        {machine.brand && machine.model && (
                          <p className="text-sm text-[#a3a3a3]">{machine.brand} · {machine.model}</p>
                        )}
                      </div>
                    </div>
                    <Link to="/laser/setup">
                      <Button variant="outline" size="sm" className="border-zinc-600 text-zinc-300 hover:border-[#e67e22] hover:text-[#e67e22]">
                        <Settings2 className="w-3.5 h-3.5 mr-1" />
                        Edit
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Type + Power badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {machine.laser_type && (
                      <Badge className="bg-[#e67e22]/20 text-[#e67e22] border border-[#e67e22]/30 text-xs uppercase">
                        {machine.laser_type}
                      </Badge>
                    )}
                    {machine.power_watts && (
                      <Badge className="bg-black/60 text-white border border-[#3a3a3a] text-xs">
                        {machine.power_watts}W
                      </Badge>
                    )}
                    {machine.has_air_assist && (
                      <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30 text-xs">
                        Air Assist
                      </Badge>
                    )}
                    {machine.cooling_type && (
                      <Badge className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 text-xs capitalize">
                        {machine.cooling_type} cooled
                      </Badge>
                    )}
                  </div>

                  {/* Spec grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {machine.bed_width && machine.bed_height && (
                      <div className="bg-black/60 rounded-sm p-2">
                        <div className="flex items-center gap-1 mb-1">
                          <Ruler className="w-3 h-3 text-[#a3a3a3]" />
                          <span className="text-xs text-[#a3a3a3]">Bed Size</span>
                        </div>
                        <div className="text-sm text-white font-medium">
                          {machine.bed_width}×{machine.bed_height}mm
                        </div>
                      </div>
                    )}
                    {machine.max_speed && (
                      <div className="bg-black/60 rounded-sm p-2">
                        <div className="flex items-center gap-1 mb-1">
                          <Zap className="w-3 h-3 text-[#a3a3a3]" />
                          <span className="text-xs text-[#a3a3a3]">Max Speed</span>
                        </div>
                        <div className="text-sm text-white font-medium">{machine.max_speed}mm/s</div>
                      </div>
                    )}
                    {machine.focal_length && (
                      <div className="bg-black/60 rounded-sm p-2">
                        <div className="flex items-center gap-1 mb-1">
                          <Thermometer className="w-3 h-3 text-[#a3a3a3]" />
                          <span className="text-xs text-[#a3a3a3]">Focal Length</span>
                        </div>
                        <div className="text-sm text-white font-medium">{machine.focal_length}"</div>
                      </div>
                    )}
                    {machine.cooling_type && (
                      <div className="bg-black/60 rounded-sm p-2">
                        <div className="flex items-center gap-1 mb-1">
                          <Droplets className="w-3 h-3 text-[#a3a3a3]" />
                          <span className="text-xs text-[#a3a3a3]">Cooling</span>
                        </div>
                        <div className="text-sm text-white font-medium capitalize">{machine.cooling_type}</div>
                      </div>
                    )}
                    {machine.has_air_assist !== undefined && (
                      <div className="bg-black/60 rounded-sm p-2">
                        <div className="flex items-center gap-1 mb-1">
                          <Wind className="w-3 h-3 text-[#a3a3a3]" />
                          <span className="text-xs text-[#a3a3a3]">Air Assist</span>
                        </div>
                        <div className="text-sm text-white font-medium">{machine.has_air_assist ? 'Yes' : 'No'}</div>
                      </div>
                    )}
                    {machine.controller && (
                      <div className="bg-black/60 rounded-sm p-2">
                        <div className="flex items-center gap-1 mb-1">
                          <Settings2 className="w-3 h-3 text-[#a3a3a3]" />
                          <span className="text-xs text-[#a3a3a3]">Controller</span>
                        </div>
                        <div className="text-sm text-white font-medium">{machine.controller}</div>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {machine.notes && (
                    <div className="mt-3 p-2 bg-black/40 rounded-sm border border-[#3a3a3a]/50">
                      <p className="text-xs text-[#a3a3a3]">{machine.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick actions */}
        {machines.length > 0 && (
          <div className="bg-black/80 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-3">Quick Actions</div>
            <div className="flex flex-wrap gap-3">
              <Link to="/laser/calculator">
                <Button variant="outline" size="sm" className="border-zinc-600 text-zinc-300 hover:border-[#e67e22] hover:text-[#e67e22]">
                  Open Calculator
                  <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
              <Link to="/laser/troubleshooting">
                <Button variant="outline" size="sm" className="border-zinc-600 text-zinc-300 hover:border-[#e67e22] hover:text-[#e67e22]">
                  Troubleshooting Guide
                  <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
              <Link to="/laser/materials">
                <Button variant="outline" size="sm" className="border-zinc-600 text-zinc-300 hover:border-[#e67e22] hover:text-[#e67e22]">
                  Material Guide
                  <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LaserTools;
